
import { logger } from '@/utils/logger';

/**
 * Validate OpenAI API key
 */
export function validateApiKey(apiKey: string | undefined): void {
  if (!apiKey) {
    const error = new Error('API key is required for FAQ generation');
    logger.error('API key is required for FAQ generation', error, {
      component: 'faqExpertService',
      timestamp: new Date() // Fixed: Using Date object instead of string
    });
    throw error;
  }
  
  if (!apiKey.startsWith('sk-') || apiKey.length < 20) {
    const error = new Error('Invalid API key format. OpenAI API keys should start with "sk-" and be at least 20 characters long.');
    logger.error('Invalid API key format for FAQ generation', error, {
      component: 'faqExpertService',
      context: {
        keyLength: apiKey.length,
        keyPrefix: apiKey.substring(0, 3),
        timestamp: new Date() // Fixed: Using Date object instead of string
      }
    });
    throw error;
  }
  
  // Log success but don't expose full key
  const maskedKey = `${apiKey.substring(0, 5)}...${apiKey.substring(apiKey.length - 4)}`;
  logger.info('API key validation passed', {
    component: 'faqExpertService',
    context: {
      maskedKey,
      keyLength: apiKey.length,
      timestamp: new Date() // Fixed: Using Date object instead of string
    }
  });
}

/**
 * Format API error for better diagnostics
 */
export function formatApiError(error: any): Error {
  // If it's already an Error object
  if (error instanceof Error) {
    return error;
  }
  
  // If it's an API response error
  if (error.response) {
    const status = error.response.status;
    const statusText = error.response.statusText;
    const data = error.response.data || {};
    
    // Format based on status code
    if (status === 401) {
      return new Error(`API authentication failed (401): ${data.error?.message || statusText}`);
    }
    
    if (status === 429) {
      return new Error(`API rate limit exceeded (429): ${data.error?.message || statusText}`);
    }
    
    if (status === 500) {
      return new Error(`API server error (500): ${data.error?.message || statusText}`);
    }
    
    return new Error(`API error ${status}: ${data.error?.message || statusText}`);
  }
  
  // If it's a network error
  if (error.request) {
    return new Error(`Network error: No response received from API`);
  }
  
  // For everything else
  return new Error(`Error: ${error.message || 'Unknown error'}`);
}
